/**
 * Vocabulário do domínio societário.
 *
 * O nome que aparece na tela é o nome que o contador usa no cartório e na
 * Junta. Nunca traduzir para "empresa", "dono", "documento": quem opera o
 * sistema fala sócio, quota, cláusula, ato. Ver `.claude/skills/social-dominio`.
 */
/**
 * Tipo societário. Determina QUAIS cláusulas se aplicam e QUAIS regras de
 * validação valem — não é rótulo, é chave de roteamento do motor de cláusulas.
 */
export const TIPOS_SOCIETARIOS = [
    'SLU', // Sociedade Limitada Unipessoal — exatamente 1 sócio
    'LTDA', // Sociedade Limitada pluripessoal — 2 ou mais sócios
    'EI', // Empresário Individual — requerimento, NÃO contrato social
    'SS', // Sociedade Simples
    'SA', // Sociedade Anônima — estatuto, ações, NÃO quotas
    // Sociedade em Nome Coletivo (arts. 1.039-1.044 CC): só pessoas físicas,
    // 2+ sócios, responsabilidade ILIMITADA e SOLIDÁRIA de todos — o oposto da
    // LTDA —, administração restrita a sócio, firma com o nome de um ou mais
    // deles (não denominação).
    'NC',
    // Sociedade em Comandita Simples (arts. 1.045-1.051 CC): dois papéis de
    // sócio — comanditado (pessoa física, responsabilidade ilimitada e
    // solidária, administra) e comanditário (responde só pelo valor da quota,
    // NÃO administra; se administrar, perde a limitação). Exige ao menos um de
    // cada.
    'CS',
];
export const NOME_TIPO_SOCIETARIO = {
    SLU: 'Sociedade Limitada Unipessoal',
    LTDA: 'Sociedade Limitada',
    EI: 'Empresário Individual',
    SS: 'Sociedade Simples',
    SA: 'Sociedade Anônima',
    NC: 'Sociedade em Nome Coletivo',
    CS: 'Sociedade em Comandita Simples',
};
/** Tipos que se constituem por CONTRATO social (e não por estatuto/requerimento). */
export const TIPOS_COM_CONTRATO = [
    'SLU',
    'LTDA',
    'SS',
    'NC',
    'CS',
];
/**
 * Ato societário. É o EVENTO que muda o estado da sociedade — o documento é
 * apenas a projeção dele. Ver `docs/MODELO_DADOS.md`.
 */
export const TIPOS_ATO = [
    'CONSTITUICAO',
    'ALTERACAO',
    'TRANSFORMACAO',
    'DISTRATO',
    'PROCURACAO',
    'ATA',
];
export const NOME_TIPO_ATO = {
    CONSTITUICAO: 'Contrato Social',
    ALTERACAO: 'Alteração Contratual',
    TRANSFORMACAO: 'Transformação',
    DISTRATO: 'Distrato Social',
    PROCURACAO: 'Procuração',
    ATA: 'Ata de Reunião',
};
/**
 * O que uma alteração contratual pode mudar. Cada valor casa com uma cláusula
 * de alteração e com o trecho da consolidação que precisa ser regerado.
 *
 * Sem esta lista fechada, "alteração" vira texto livre e a consolidação
 * automática deixa de ser possível — que é exatamente o erro que este produto
 * existe para eliminar.
 */
export const CAMPOS_MUDANCA = [
    'NOME_EMPRESARIAL',
    'NOME_FANTASIA',
    'SEDE',
    'OBJETO_SOCIAL',
    'CNAE',
    'CAPITAL',
    'INGRESSO_SOCIO',
    'SAIDA_SOCIO',
    'CESSAO_QUOTAS',
    // Mudança do percentual/quantidade de quotas de cada sócio SEM entrada ou
    // saída — por exemplo aumento de capital subscrito por um só sócio. NÃO É
    // `CESSAO_QUOTAS`: cessão é quota mudando de DONO; isto é o percentual
    // mudando sem troca de titular.
    'PARTICIPACAO_SOCIETARIA',
    'ADMINISTRACAO',
    'PODERES_ADMINISTRADOR',
    'PRO_LABORE',
    'ENQUADRAMENTO',
    'PRAZO_DURACAO',
    'FORO',
    /**
     * Abertura ou encerramento de FILIAL.
     *
     * Um campo só para as duas, e não `ABERTURA_FILIAL`/`ENCERRAMENTO_FILIAL`:
     * a cláusula lê o `de` e o `para` para saber qual foi — `de: null` é
     * abertura, `para` com `encerramento` é fechamento. Dois valores no enum
     * exigiriam que o diff decidisse antes, e a decisão já está nos dados.
     */
    'FILIAL',
    /**
     * O regime das sociedades por AÇÕES: abertura de capital, conselho de
     * administração, conselho fiscal, dividendo mínimo, capital autorizado,
     * vantagem das preferenciais.
     *
     * Um campo para o conjunto, e não um por item: são todos reforma
     * estatutária, todos reescrevem os mesmos artigos, e a cláusula lê `de` e
     * `para` para saber qual foi. Ver `mesmoRegimeDeAcoes` em `comparar.ts`.
     */
    'REGIME_ACOES',
];
export const NOME_CAMPO_MUDANCA = {
    NOME_EMPRESARIAL: 'do nome empresarial',
    NOME_FANTASIA: 'do nome fantasia',
    SEDE: 'do endereço da sede',
    OBJETO_SOCIAL: 'do objeto social',
    CNAE: 'das atividades econômicas',
    CAPITAL: 'do capital social',
    INGRESSO_SOCIO: 'de ingresso de sócio',
    SAIDA_SOCIO: 'de retirada de sócio',
    CESSAO_QUOTAS: 'de cessão e transferência de quotas',
    PARTICIPACAO_SOCIETARIA: 'da participação societária',
    ADMINISTRACAO: 'da administração',
    PODERES_ADMINISTRADOR: 'dos poderes do administrador',
    PRO_LABORE: 'do pró-labore',
    ENQUADRAMENTO: 'do enquadramento',
    PRAZO_DURACAO: 'do prazo de duração',
    FORO: 'do foro de eleição',
    FILIAL: 'de filial',
    REGIME_ACOES: 'do regime das ações',
};
/**
 * Enquadramento pela LC 123/2006. Note que é declaração de RECEITA BRUTA
 * anual, não de capital social — confundir os dois é erro comum e o validador
 * o denuncia (regra `enquadramento-incoerente`).
 */
export const ENQUADRAMENTOS = ['MEI', 'ME', 'EPP', 'DEMAIS'];
export const NOME_ENQUADRAMENTO = {
    MEI: 'Microempreendedor Individual - MEI',
    ME: 'Microempresa - ME',
    EPP: 'Empresa de Pequeno Porte - EPP',
    DEMAIS: 'Demais',
};
/** Teto de receita bruta anual, em reais, por enquadramento (LC 123/2006). */
export const TETO_RECEITA_ENQUADRAMENTO = {
    MEI: 81_000,
    ME: 360_000,
    EPP: 4_800_000,
    DEMAIS: null,
};
/** Situação de um ato no fluxo do escritório. */
export const SITUACOES_ATO = [
    'RASCUNHO',
    'VALIDADO',
    'GERADO',
    'ASSINADO',
    'PROTOCOLADO',
    'REGISTRADO',
    'EXIGENCIA',
    'CANCELADO',
];
//# sourceMappingURL=vocabulario.js.map